import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import config from '../../../config';
import { getCenterIdByDomain, fetchShorts } from '../../../services/videos';
import { createClient } from '@supabase/supabase-js';
import { extractVideoId } from '../../../services/youtubeService';

/**
 * Custom hook for fetching and managing shorts data
 * Fetches from Google Sheets and processes for display
 */
const useShortsData = (googleSheetId, currentSeries = 'all', centerConfig = null) => {
  console.log('[DEBUG useShortsData] Hook called with:', { googleSheetId, currentSeries, centerConfig });
  
  const [shorts, setShorts] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasLoadedYouTube, setHasLoadedYouTube] = useState(false);
  const [supabaseOffset, setSupabaseOffset] = useState(0);
  const [hasMoreSupabase, setHasMoreSupabase] = useState(true);
  const [centerId, setCenterId] = useState(null);

  // Extract Google Sheet ID from URL if needed
  const extractSheetId = useCallback((input) => {
    if (!input) return '';
    if (!input.includes('http')) return input;
    
    const match = input.match(/\/d\/([a-zA-Z0-9-_]+)/);
    return match && match[1] ? match[1] : input;
  }, []);

  // Fetch shorts from YouTube channel with pagination support
  const fetchShortsFromYouTube = useCallback(async (channelUrl, pageToken = null) => {
    if (!channelUrl) return { shorts: [], nextPageToken: null };

    try {
      // Extract channel handle/ID from URL
      let channelHandle = '';
      if (channelUrl.includes('@')) {
        // Handle format: https://www.youtube.com/@username
        channelHandle = channelUrl.split('@')[1].split('/')[0];
      } else {
        // Legacy format or direct channel ID
        channelHandle = channelUrl.split('/').pop();
      }
      
      console.log('Fetching shorts from YouTube channel:', channelHandle);
      
      const API_KEY = config.GOOGLE_API_KEY;
      const maxResults = 50; // Maximum allowed by YouTube API per page
      
      let realChannelId = '';
      
      // First, search for the channel to get the actual channel ID
      if (channelUrl.includes('@')) {
        console.log('Searching for channel handle:', channelHandle);
        const searchResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/search?part=snippet&type=channel&q=${channelHandle}&key=${API_KEY}&maxResults=1`
        );
        
        if (!searchResponse.ok) {
          throw new Error(`Channel search failed: ${searchResponse.status} ${searchResponse.statusText}`);
        }
        
        const searchData = await searchResponse.json();
        console.log('Channel search response:', searchData);
        
        if (!searchData.items || searchData.items.length === 0) {
          throw new Error(`Channel not found for handle: ${channelHandle}`);
        }
        
        realChannelId = searchData.items[0].id.channelId;
        console.log('Found real channel ID:', realChannelId);
      } else {
        // Assume it's already a channel ID or try it directly
        realChannelId = channelHandle;
      }
      
      // Get the channel's uploads playlist ID
      console.log('Fetching channel details for ID:', realChannelId);
      const channelResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=contentDetails,snippet&id=${realChannelId}&key=${API_KEY}`
      );
      
      if (!channelResponse.ok) {
        throw new Error(`Channel details fetch failed: ${channelResponse.status} ${channelResponse.statusText}`);
      }
      
      const channelData = await channelResponse.json();
      console.log('Channel data response:', channelData);
      
      if (!channelData.items || channelData.items.length === 0) {
        throw new Error(`Channel not found for ID: ${realChannelId}`);
      }
      
      const uploadsPlaylistId = channelData.items[0].contentDetails.relatedPlaylists.uploads;
      const channelTitle = channelData.items[0].snippet.title;
      
      console.log('Uploads playlist ID:', uploadsPlaylistId);
      
      // Fetch videos from uploads playlist with pagination
      let url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${uploadsPlaylistId}&maxResults=${maxResults}&key=${API_KEY}`;
      if (pageToken) {
        url += `&pageToken=${pageToken}`;
      }
      
      const videosResponse = await fetch(url);
      
      if (!videosResponse.ok) {
        throw new Error(`Playlist videos fetch failed: ${videosResponse.status} ${videosResponse.statusText}`);
      }
      
      const videosData = await videosResponse.json();
      console.log('Videos data response:', videosData);
      
      if (!videosData.items || videosData.items.length === 0) {
        console.warn('No videos found in channel uploads');
        return [];
      }
      
      // Get video IDs to fetch duration information
      const videoIds = videosData.items.map(item => item.snippet.resourceId.videoId).join(',');
      
      // Fetch video details to get duration
      console.log('Fetching video details to identify shorts...');
      const videoDetailsResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=contentDetails,snippet&id=${videoIds}&key=${API_KEY}`
      );
      
      if (!videoDetailsResponse.ok) {
        throw new Error(`Video details fetch failed: ${videoDetailsResponse.status}`);
      }
      
      const videoDetailsData = await videoDetailsResponse.json();
      const videoDetailsMap = {};
      videoDetailsData.items.forEach(item => {
        videoDetailsMap[item.id] = item;
      });
      
      // Filter for shorts based on duration (< 60 seconds) or title/description
      const shorts = videosData.items
        .filter(item => {
          const videoId = item.snippet.resourceId.videoId;
          const videoDetails = videoDetailsMap[videoId];
          if (!videoDetails) return false;
          
          const duration = videoDetails.contentDetails.duration;
          // Parse ISO 8601 duration (e.g., PT58S = 58 seconds)
          const match = duration.match(/PT(?:(\d+)M)?(\d+)S/);
          const minutes = parseInt(match?.[1] || '0');
          const seconds = parseInt(match?.[2] || '0');
          const totalSeconds = minutes * 60 + seconds;
          
          // Consider as shorts if:
          // 1. Duration is 3 minutes (180 seconds) or less
          // 2. Exclude live videos
          const isShortDuration = totalSeconds <= 180; // 3 minutes max
          const title = item.snippet.title.toLowerCase();
          const description = (item.snippet.description || '').toLowerCase();
          const hasShortsTags = title.includes('#shorts') || description.includes('#shorts');
          const isLive = videoDetails.snippet.liveBroadcastContent !== 'none';
          
          // Must be under 3 minutes AND not a live video
          return isShortDuration && !isLive;
        })
        .map((item, index) => {
          const videoId = item.snippet.resourceId.videoId;
          return {
            id: `yt_short_${videoId}`,
            shortUrl: `https://youtube.com/shorts/${videoId}`,
            title: item.snippet.title,
            series: 'All', // Use 'All' series for YouTube channel shorts
            eventName: 'All', // Same for YouTube shorts
            speakerName: channelTitle,
            lectureId: null,
            orderIndex: index,
            publishedAt: item.snippet.publishedAt,
            videoId: videoId
          };
        });
      
      console.log(`Found ${shorts.length} potential shorts from YouTube channel`);
      return {
        shorts,
        nextPageToken: videosData.nextPageToken || null
      };
      
    } catch (err) {
      console.error('Error fetching shorts from YouTube:', err);
      console.error('Full error details:', {
        message: err.message,
        channelUrl,
        stack: err.stack
      });
      // Don't return empty result - let the error bubble up so we can see it in the UI
      throw new Error(`YouTube channel fetch failed: ${err.message}`);
    }
  }, []);

  // Fetch shorts data from Google Sheets
  const fetchShortsFromSheets = useCallback(async (sheetId) => {
    if (!sheetId) return [];

    try {
      const cleanId = extractSheetId(sheetId);
      const RANGE = 'A:Z'; // Fetch all columns to be flexible
      const API_KEY = config.GOOGLE_API_KEY;
      
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${cleanId}/values/${RANGE}?key=${API_KEY}`
      );
      
      if (!response.ok) {
        throw new Error(`Google Sheets API request failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.values || data.values.length <= 1) {
        console.warn('No shorts data found in spreadsheet');
        return [];
      }
      
      const headers = data.values[0];
      
      // Find column indexes for shorts data
      const findColumnIndex = (possibleNames) => {
        for (const name of possibleNames) {
          const index = headers.findIndex(header => 
            header && typeof header === 'string' && 
            header.toLowerCase().includes(name.toLowerCase())
          );
          if (index >= 0) return index;
        }
        return -1;
      };
      
      const shortUrlIndex = findColumnIndex(['shortUrl', 'short_url', 'short url', 'shorts url', 'summary video', 'summaryVideoURL', 'SummaryVideoURL']);
      const titleIndex = findColumnIndex(['title', 'short title', 'video title', 'Title']);
      const eventIndex = findColumnIndex(['Event', 'event']); // Specifically look for Event column
      const lectureSeriesIdIndex = findColumnIndex(['Lecture Series ID', 'lecture_series_id', 'series_id', 'Series ID']);
      // IMPORTANT: Use 'event' for display (human-readable), 'lectureSeriesId' is a technical identifier
      // This aligns with googleSheets.js pattern where series = event for display purposes
      const seriesIndex = eventIndex; // Always use Event for series display name
      const speakerIndex = findColumnIndex(['speaker', 'speakerName', 'speaker_name', 'sheikh', 'Speaker']);
      const lectureIdIndex = findColumnIndex(['lectureId', 'lecture_id', 'lecture id', 'full lecture', 'Lecture ID', 'L-ID', 'lecture link', 'full video']);
      const orderIndex = findColumnIndex(['orderIndex', 'order_index', 'order', 'sort', 'L-ID in Series']);
      const dateIndex = findColumnIndex(['publishedAt', 'published_at', 'date', 'publish date', 'upload date']);
      
      console.log('Shorts column mapping:', {
        shortUrl: shortUrlIndex,
        title: titleIndex,
        event: eventIndex,
        lectureSeriesId: lectureSeriesIdIndex,
        series: seriesIndex,
        speaker: speakerIndex,
        lectureId: lectureIdIndex,
        order: orderIndex,
        date: dateIndex
      });
      
      // Process rows into shorts objects
      const processedShorts = data.values.slice(1)
        .map((row, index) => {
          // Skip empty rows
          if (!row.length || !row[shortUrlIndex]) return null;
          
          const shortUrl = row[shortUrlIndex]?.trim();
          // Check if it's a YouTube shorts URL or any YouTube video URL
          if (!shortUrl || (!shortUrl.includes('youtube.com') && !shortUrl.includes('youtu.be'))) return null;
          
          // Extract video ID for consistent component keys
          const videoId = extractVideoId(shortUrl);
          
          return {
            id: videoId || `short_${index}`, // Use video ID if extractable, fallback to index
            shortUrl: shortUrl.includes('/shorts/') ? shortUrl : `https://youtube.com/shorts/${videoId || ''}`,
            title: row[titleIndex]?.trim() || `Short ${index + 1}`,
            series: row[seriesIndex]?.trim() || 'Uncategorized', // Uses Event (human-readable) for display
            eventName: row[eventIndex]?.trim() || row[seriesIndex]?.trim() || 'Uncategorized', // Store Event name separately
            speakerName: row[speakerIndex]?.trim() || 'Speaker',
            lectureId: row[lectureIdIndex]?.trim() || null,
            orderIndex: parseInt(row[orderIndex]) || 0,
            publishedAt: row[dateIndex]?.trim() || null
          };
        })
        .filter(Boolean); // Remove null entries
      
      console.log(`Processed ${processedShorts.length} shorts from Google Sheets`);
      return processedShorts;
      
    } catch (err) {
      console.error('Error fetching shorts from Google Sheets:', err);
      throw err;
    }
  }, [extractSheetId]);
  
  // Helper function to extract video ID from YouTube URL
  const extractVideoId = (url) => {
    if (!url) return null;
    
    // Handle different YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^#&?]*).*/,
      /(?:youtube\.com\/shorts\/)([^#&?]*).*/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  // Sync shorts data to Supabase
  const syncToSupabase = useCallback(async (shortsData) => {
    try {
      // Clear existing data (simple approach for Phase 1)
      await supabase.from('shorts_metadata').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // Insert new data
      if (shortsData.length > 0) {
        const { error } = await supabase
          .from('shorts_metadata')
          .insert(shortsData.map(short => ({
            short_url: short.shortUrl,
            title: short.title,
            speaker_name: short.speakerName,
            series: short.series,
            lecture_id: short.lectureId,
            order_index: short.orderIndex
          })));
        
        if (error) {
          console.error('Error syncing to Supabase:', error);
        } else {
          console.log('Successfully synced shorts to Supabase');
        }
      }
    } catch (err) {
      console.error('Error in Supabase sync:', err);
    }
  }, []);

  // Extract stable values from centerConfig
  const centerDomain = centerConfig?.domain || '';
  const centerName = centerConfig?.name || 'Speaker';
  const configCenterId = centerConfig?.center_id || null;
  
  // Store all shorts data (unfiltered)
  const [allShortsData, setAllShortsData] = useState([]);
  
  // Cache to preserve data between re-renders
  const dataCache = useRef(null);
  const lastFetchParams = useRef(null);
  
  // Fetch data
  const fetchData = useCallback(async () => {
    console.log('[DEBUG fetchData] Called!');
    
    // Check if we should skip fetch (same parameters)
    const currentParams = { googleSheetId, configCenterId };
    const currentParamsStr = JSON.stringify(currentParams);
    const lastParamsStr = lastFetchParams.current ? JSON.stringify({ 
      googleSheetId: lastFetchParams.current.googleSheetId, 
      configCenterId: lastFetchParams.current.configCenterId 
    }) : null;
    
    if (lastParamsStr === currentParamsStr && dataCache.current) {
      console.log('[DEBUG] Skipping fetch - parameters unchanged');
      setAllShortsData(dataCache.current);
      setLoading(false);
      return;
    }
    
    lastFetchParams.current = currentParams;
    setLoading(true);
    setError(null);
    
    console.log('useShortsData: domain:', centerDomain);
    
    try {
      let shortsData = [];
      let youtubeShorts = [];
      
      // Fetch from Google Sheets first
      if (googleSheetId) {
        try {
          shortsData = await fetchShortsFromSheets(googleSheetId);
          console.log(`Fetched ${shortsData.length} shorts from Google Sheets`);
        } catch (sheetsError) {
          console.warn('Failed to fetch from Google Sheets:', sheetsError);
        }
      } else {
        console.log('[DEBUG] No googleSheetId provided, skipping Google Sheets fetch');
      }
      
      // Always fetch from Supabase to get "all" category data
      console.log('[DEBUG] Attempting to fetch shorts from Supabase...');
      
      try {
        // Get center ID if not already fetched
        let currentCenterId = centerId;
        if (!currentCenterId) {
          // First try to use center_id from config
          if (configCenterId) {
            console.log('[DEBUG] Using center_id from config:', configCenterId);
            currentCenterId = configCenterId;
            setCenterId(currentCenterId);
          } else {
            // Fallback to domain lookup if no center_id in config
            const domain = centerDomain || window.location.hostname;
            console.log('[DEBUG] No center_id in config, looking up by domain:', domain);
            
            currentCenterId = await getCenterIdByDomain(domain);
            console.log('[DEBUG] getCenterIdByDomain returned:', currentCenterId);
            
            if (currentCenterId) {
              setCenterId(currentCenterId);
              console.log('[DEBUG] Successfully found center ID:', currentCenterId);
            } else {
              console.error('[DEBUG] Failed to get center ID for domain:', domain);
            }
          }
        }
        
        if (currentCenterId) {
          console.log('[DEBUG] Calling fetchShorts with centerId:', currentCenterId);
          const result = await fetchShorts(currentCenterId, 0, 20);
          console.log('[DEBUG] fetchShorts returned:', result);
          console.log('[DEBUG] Number of videos returned:', result.videos?.length || 0);
          
          if (result.videos && result.videos.length > 0) {
            console.log('[DEBUG] First video from Supabase:', result.videos[0]);
          }
          
          // Transform Supabase videos to match the expected format
          youtubeShorts = result.videos.map((video, index) => ({
            id: video.youtube_video_id,  // Use consistent ID format to prevent remounting
            shortUrl: `https://youtube.com/shorts/${video.youtube_video_id}`,
            title: video.title,
            series: 'all', // Use lowercase for consistency
            eventName: 'all', // Use lowercase for consistency
            speakerName: centerName,
            lectureId: null,
            orderIndex: index,
            publishedAt: video.published_at,
            videoId: video.youtube_video_id,
            thumbnail: video.thumbnail_url
          }));
          
          console.log('[DEBUG] Transformed Supabase shorts:', youtubeShorts.length);
          if (youtubeShorts.length > 0) {
            console.log('[DEBUG] First transformed short:', youtubeShorts[0]);
          }
          
          setSupabaseOffset(20);
          setHasMoreSupabase(result.hasMore);
          setHasLoadedYouTube(true);
          
          console.log(`[DEBUG] Successfully fetched ${youtubeShorts.length} shorts from Supabase`);
          if (result.hasMore) {
            console.log('[DEBUG] More videos available in Supabase');
          }
          
          if (youtubeShorts.length === 0) {
            console.warn('[DEBUG] No shorts found in Supabase videos table - check video_type and privacy_status filters');
          }
        } else {
          console.warn('[DEBUG] Could not find center ID for domain:', centerDomain || window.location.hostname);
          console.log('[DEBUG] Continuing with Google Sheets data only');
        }
      } catch (supabaseError) {
        console.error('[DEBUG] Failed to fetch from Supabase:', supabaseError);
        console.error('[DEBUG] Error details:', supabaseError.message, supabaseError.stack);
        // Only show error if we have no other data
        if (shortsData.length === 0) {
          setError(`Supabase fetch failed: ${supabaseError.message}`);
        } else {
          console.log('[DEBUG] Supabase fetch failed but we have Google Sheets data');
        }
      }
      
      // Merge data from both sources
      let allShorts = [...shortsData];
      console.log(`[DEBUG] Starting with ${shortsData.length} shorts from Google Sheets`);
      
      // Add YouTube shorts that aren't already in the sheets data
      if (youtubeShorts.length > 0) {
        console.log(`[DEBUG] Found ${youtubeShorts.length} shorts from Supabase`);
        const existingUrls = new Set(shortsData.map(s => s.shortUrl));
        console.log('[DEBUG] Existing URLs from Google Sheets:', Array.from(existingUrls).slice(0, 3));
        console.log('[DEBUG] New URLs from Supabase:', youtubeShorts.slice(0, 3).map(s => s.shortUrl));
        
        const newYouTubeShorts = youtubeShorts.filter(s => !existingUrls.has(s.shortUrl));
        console.log(`[DEBUG] ${newYouTubeShorts.length} Supabase shorts are not duplicates`);
        
        allShorts = [...allShorts, ...newYouTubeShorts];
        console.log(`[DEBUG] Total shorts after merge: ${allShorts.length}`);
      } else {
        console.log('[DEBUG] No youtubeShorts to merge');
      }
      
      // If still no data, try Supabase fallback
      if (allShorts.length === 0) {
        console.log('No shorts from primary sources, trying Supabase fallback');
        const { data: supabaseData, error: supabaseError } = await supabase
          .from('shorts_metadata')
          .select('*')
          .order('series', { ascending: true })
          .order('order_index', { ascending: true });
        
        if (supabaseError) {
          throw supabaseError;
        }
        
        allShorts = supabaseData?.map(item => ({
          id: item.id,
          shortUrl: item.short_url,
          title: item.title,
          series: item.series,
          speakerName: item.speaker_name,
          lectureId: item.lecture_id,
          orderIndex: item.order_index
        })) || [];
      }
      
      // Skip Supabase sync for now - RLS policies need to be configured
      // This can be re-enabled when the backend is properly set up
      /*
      // Sync to Supabase for backup and likes functionality
      if (allShorts.length > 0) {
        await syncToSupabase(allShorts);
      }
      */
      
      // Sort all shorts by publish date (latest first)
      allShorts.sort((a, b) => {
        // If publishedAt exists from YouTube, use it
        if (a.publishedAt && b.publishedAt) {
          return new Date(b.publishedAt) - new Date(a.publishedAt);
        }
        // Otherwise fall back to orderIndex (reverse order for latest first)
        return (b.orderIndex || 0) - (a.orderIndex || 0);
      });
      
      // Check if we got data or should use cache
      if (allShorts.length === 0 && dataCache.current && dataCache.current.length > 0) {
        console.log('[DEBUG] No data from fetch, using cache with', dataCache.current.length, 'items');
        setAllShortsData(dataCache.current);
        // Use cached series data too
        const cachedSeries = series.length === 0 ? lastFetchParams.current?.series || [] : series;
        setSeries(cachedSeries);
      } else {
        // Store all shorts data (unfiltered) for client-side filtering
        setAllShortsData(allShorts);
        
        // Update cache if we have data
        if (allShorts.length > 0) {
          dataCache.current = allShorts;
        }
        
        // Extract unique series with their latest publish dates
        const seriesMap = new Map();
        allShorts.forEach(short => {
          const seriesName = short.series;
          if (seriesName && seriesName !== 'all' && seriesName !== 'All') {
            const existingDate = seriesMap.get(seriesName);
            const shortDate = short.publishedAt ? new Date(short.publishedAt) : new Date(0);
            if (!existingDate || shortDate > existingDate) {
              seriesMap.set(seriesName, shortDate);
            }
          }
        });
        
        // Sort series by their latest video date (newest first)
        const uniqueSeries = Array.from(seriesMap.entries())
          .sort((a, b) => b[1] - a[1])
          .map(entry => entry[0]);
        
        setSeries(uniqueSeries);
        
        // Cache series data too
        if (lastFetchParams.current) {
          lastFetchParams.current.series = uniqueSeries;
        }
      }
      
      console.log(`[DEBUG] Total shorts available: ${allShortsData.length || allShorts.length}`);
      
      // Clear any errors if we have data
      if ((allShorts.length > 0) || (dataCache.current && dataCache.current.length > 0)) {
        setError(null);
      }
      
    } catch (err) {
      console.error('Error fetching shorts data:', err);
      setError(err.message || 'Failed to load shorts data');
    } finally {
      setLoading(false);
    }
  }, [googleSheetId, configCenterId]); // Only depend on essential IDs that determine what data to fetch

  // Load more videos from Supabase
  const loadMoreVideos = useCallback(async () => {
    if (!hasMoreSupabase || loadingMore || !hasLoadedYouTube || currentSeries !== 'all' || !centerId) return;
    
    setLoadingMore(true);
    console.log('Loading more videos from Supabase, offset:', supabaseOffset);
    
    try {
      const result = await fetchShorts(centerId, supabaseOffset, 20);
      
      if (result.videos.length > 0) {
        // Transform and add new shorts to existing list
        const newShorts = result.videos.map((video, index) => ({
          id: video.youtube_video_id, // Use consistent ID format
          shortUrl: `https://youtube.com/shorts/${video.youtube_video_id}`,
          title: video.title,
          series: 'all', // Use lowercase for consistency
          eventName: 'all', // Use lowercase for consistency
          speakerName: centerConfig?.name || 'Speaker',
          lectureId: null,
          orderIndex: supabaseOffset + index,
          publishedAt: video.published_at,
          videoId: video.youtube_video_id,
          thumbnail: video.thumbnail_url
        }));
        
        // Avoid duplicates
        const existingUrls = new Set(allShortsData.map(s => s.shortUrl));
        const uniqueNewShorts = newShorts.filter(s => !existingUrls.has(s.shortUrl));
        
        setAllShortsData(prev => [...prev, ...uniqueNewShorts]);
        setSupabaseOffset(prev => prev + 20);
        setHasMoreSupabase(result.hasMore);
        
        console.log(`Loaded ${uniqueNewShorts.length} more shorts, total: ${allShortsData.length + uniqueNewShorts.length}`);
      }
    } catch (err) {
      console.error('Error loading more videos:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMoreSupabase, loadingMore, hasLoadedYouTube, currentSeries, centerId, supabaseOffset, allShortsData, centerConfig]);
  
  // Refetch function for error recovery
  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Reset Supabase pagination when series changes
  useEffect(() => {
    setSupabaseOffset(0);
    setHasMoreSupabase(true);
  }, [currentSeries]);

  // Debug effect lifecycle
  useEffect(() => {
    console.log('[DEBUG useEffect] MOUNT at', Date.now());
    return () => console.log('[DEBUG useEffect] UNMOUNT at', Date.now());
  }, []);

  // Initial data fetch - only once
  useEffect(() => {
    console.log('[DEBUG useEffect] About to call fetchData. Timestamp:', Date.now());
    fetchData();
  }, [fetchData]);

  // Client-side filtering based on currentSeries
  useEffect(() => {
    if (allShortsData.length === 0) {
      setShorts([]);
      return;
    }
    
    console.log('[DEBUG] Filtering data client-side. currentSeries:', currentSeries, 'allShortsData:', allShortsData.length);
    
    const filteredShorts = currentSeries === 'all' 
      ? allShortsData 
      : allShortsData.filter(short => short.series === currentSeries);
      
    console.log('[DEBUG] Client-side filtering complete. Filtered:', filteredShorts.length);
    
    setShorts(filteredShorts);
  }, [currentSeries, allShortsData]);

  return {
    shorts,
    series,
    loading,
    error,
    refetch,
    loadMoreVideos,
    hasMoreVideos: currentSeries === 'all' ? hasMoreSupabase : !!nextPageToken,
    loadingMore
  };
};

export default useShortsData;